import React, { useState, useEffect } from "react";
import { Card, Typography, Button, Table, Tag, Segmented, Space } from "antd";
import api from "../../services/api";

const { Text } = Typography;

const iapEventColor = (type) => {
  const value = String(type || "").toUpperCase();
  if (["SUBSCRIBED", "DID_RENEW", "OFFER_REDEEMED", "RENEWAL_EXTENDED"].includes(value)) {
    return "green";
  }
  if (["EXPIRED", "REFUND", "REVOKE", "DID_FAIL_TO_RENEW", "GRACE_PERIOD_EXPIRED"].includes(value)) {
    return "red";
  }
  return "blue";
};

const iapColumns = [
  {
    title: "Date",
    dataIndex: "signedDateISO",
    key: "date",
    render: (value) => (value ? new Date(value).toLocaleString() : "-"),
  },
  {
    title: "Event",
    dataIndex: "notificationType",
    key: "event",
    render: (value, record) => (
      <>
        <Tag color={iapEventColor(value)}>{value || "-"}</Tag>
        {record.subtype ? <Tag>{record.subtype}</Tag> : null}
      </>
    ),
  },
  {
    title: "Product",
    key: "product",
    render: (_, record) => record?.transactionInfo?.productId || "-",
  },
  {
    title: "Transaction ID",
    key: "transactionId",
    render: (_, record) => record?.transactionInfo?.transactionId || "-",
  },
  {
    title: "Original Txn ID",
    key: "originalTransactionId",
    render: (_, record) => record?.transactionInfo?.originalTransactionId || "-",
  },
  {
    title: "Price",
    key: "price",
    render: (_, record) => {
      const price = record?.transactionInfo?.price;
      const currency = record?.transactionInfo?.currency;
      if (price === null || price === undefined) {
        return "-";
      }
      // Apple reports price in milliunits of the currency.
      return `${(Number(price) / 1000).toFixed(2)} ${currency || ""}`.trim();
    },
  },
  {
    title: "Expires",
    key: "expires",
    render: (_, record) => {
      const expiresDate = record?.transactionInfo?.expiresDate;
      return expiresDate ? new Date(Number(expiresDate)).toLocaleString() : "-";
    },
  },
  {
    title: "Env",
    dataIndex: "environment",
    key: "environment",
    render: (value) =>
      value ? (
        <Tag color={String(value).toLowerCase() === "production" ? "purple" : "orange"}>
          {value}
        </Tag>
      ) : (
        "-"
      ),
  },
];

const AppleInAppPayments = ({
  pageSize = 10,
  title = "Apple In-App Purchases",
  productId = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState("");
  // Default view is Sandbox; ALL merges sandbox + production into one list.
  const [environment, setEnvironment] = useState("SANDBOX");

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError("");
      // Apple's notification history is paginated server-side; allow extra time.
      const response = await api.get(
        "/admin/app-store/payment-history",
        {
          timeout: 60000,
          params: {
            ...(productId ? { productId } : {}),
            environment,
          },
        }
      );
      const data = response?.data || {};
      setPayments(Array.isArray(data.notifications) ? data.notifications : []);
    } catch (err) {
      console.error("Error fetching in-app purchase history:", err);
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to load in-app purchase history";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, environment]);

  return (
    <Card
      title={title}
      extra={
        <Space>
          <Segmented
            options={[
              { label: "All", value: "ALL" },
              { label: "Sandbox", value: "SANDBOX" },
              { label: "Production", value: "PRODUCTION" },
            ]}
            value={environment}
            onChange={(value) => setEnvironment(value)}
            disabled={loading}
          />
          <Button loading={loading} onClick={fetchPayments}>
            Refresh
          </Button>
        </Space>
      }
    >
      {error ? (
        <Text type="danger" style={{ display: "block", marginBottom: "12px" }}>
          {error}
        </Text>
      ) : null}
      <Table
        columns={iapColumns}
        dataSource={payments}
        loading={loading}
        rowKey={(record, index) =>
          record?.notificationUUID ||
          `${record?.transactionInfo?.transactionId || "iap"}-${index}`
        }
        pagination={{ pageSize, showSizeChanger: false }}
        scroll={{ x: 1100 }}
        size="small"
        expandable={{
          expandedRowRender: (record) => (
            <pre style={{ margin: 0, maxHeight: "400px", overflow: "auto" }}>
              {JSON.stringify(record, null, 2)}
            </pre>
          ),
        }}
      />
    </Card>
  );
};

export default AppleInAppPayments;
